<template>
  <div id="radar-grid-layer">
    <rg-last-object-tracking
      :start-time="lastObjectTracking.startTime"
      :track-no="lastObjectTracking.trackNo"
      :radarGridObjectFirestoreData="radarGridObjectFirestoreData"
      :rgRiskLevelManagerMap="rgRiskLevelManagerMap"
      :handleClickTrackingBodyBtn="changeActiveRadarGridObjectList"
      :handleClickListItem="flyAndAppendPopupObject"
      :timeCount="timeCount"
    >
    </rg-last-object-tracking>
    <rg-object-list-container
      :isActiveSidebar="isActiveRadarGridObjectList"
      :radarGridObjectFirestoreData="radarGridObjectFirestoreData"
      :rgRiskLevelManagerMap="rgRiskLevelManagerMap"
      :handleClickClose="changeActiveRadarGridObjectList"
      :handleClickListItem="flyAndAppendPopupObject"
      :timeCount="timeCount"
    >
    </rg-object-list-container>
  </div>
</template>

<script>
import Vue from "vue";
import { db } from "@/firebase/firebaseConfig.js";
import * as turfHelpers from "@turf/helpers";
import RgPopup from "./components/RgPopup.vue";
import RgZoomPic from "./components/RgZoomPic.vue";
import RgCamera from "./components/RgCamera.vue";
import RgLastObjectTracking from "./components/RgLastObjectTracking.vue";
import RgObjectListContainer from "./components/rg-object-list-container/RgObjectListContainer.vue";
import { RadarGridMixin } from "@/mixins";
import { WakeLineUtil } from "@/utils";
import moment from "moment-timezone";
import AuthService from "@/services/authService";
import radarGridObjectService from "@/services/radarGridObjectService";
import mapKeys from "lodash/mapKeys";
import chunk from "lodash/chunk";
import isString from "lodash/isString";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { Popup } from "mapbox-gl";
import { createNamespacedHelpers } from "vuex";
import { RADAR_GRID_CATEGORY } from "@/config/const";
const { mapState: mapStateOfLiveMap } = createNamespacedHelpers("liveMap");

const INTERVAL_CALC_OBJECT_ELAPSED_TIME = 1000;
const SECONDS_IN_A_DAY = 24 * 60 * 60;

export default {
  name: "RadarGridLayer",
  mixins: [RadarGridMixin],
  components: { RgLastObjectTracking, RgObjectListContainer },
  data() {
    return {
      initialized: false,
      layersMinZoom: 5,
      radarGridSourceId: "RadarGridSourceId",
      radarGridMapLayerId: "RadarGridMapLayerId",
      radarGridWakeLineSourceId: "radarGridWakeLineSourceId",
      radarGridWakeLineLayerId: "radarGridWakeLineLayerId",
      radarGridWakeFutureLineSourceId: "radarGridWakeFutureLineSourceId",
      radarGridWakeFutureLineLayerId: "radarGridWakeFutureLineLayerId",
      radarGridFrameLayerId: "radarGridFrameLayerId",
      radarGridInfo: new Map(),
      radarGridObjectFeatures: [],
      radarGridObjectFirestoreData: {}, // Object<RadarGridObject.id, RadarGridObject>
      radarGridObjectPopupIds: [],
      radarGridPrivileges: {},
      radarGridCameraMaster: {},
      popupViewInstances: new Map(),
      zoomPicViewInstances: new Map(),
      cameraViewInstances: new Map(),
      pastWakes: {}, // Record<string(radarObjectId), Array<wake>>
      futureWakes: {}, // Record<string(radarObjectId), Array<wake>>
      showLastObjectTracking: false,
      unsubscribeList: [],
      lastObjectTracking: {
        trackNo: null,
        startTime: null,
        momentObj: null,
      },
      uuid: null,
      cameraIds: [],
      cameraCtlPermissions: {},
      targetIcon: null,
      cameras: [],
      radarGridCameraMasterPulsingImageId:
        "radarGridCameraMasterPulsingImageId",
      radarGridCameraMasterPulsingManualImageId:
        "radarGridCameraMasterPulsingManualImageId",
      radarGridCameraMasterPulsingSourceId:
        "radarGridCameraMasterPulsingSourceId",
      radarGridCameraMasterSourceId: "radarGridCameraMasterSourceId",
      radarGridCameraMasterPulsingLayerId:
        "radarGridCameraMasterPulsingLayerId",
      radarGridCameraMasterLayerId: "radarGridCameraMasterLayerId",
      objectIdsToBeShowFrame: [],
      timeoutIdsOfShowFrame: {},
      isActiveRadarGridObjectList: false,
      isFocusingRadarObject: false,
      countTimer: null,
      timeCount: 0,
    };
  },

  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
  },

  watch: {
    mapEvent() {},
    async isReady() {
      if (this.isReady) {
        await this.addMapSources();
        await this.addMapLayers();
        this.filterFrameById(this.showFrameObjectFilter);
        if (!this.initialized) {
          await this.addClickEvent();
          this.setRadarCameraMasterLayerEvent();
          await Promise.allSettled([
            this.subscribeRadarGridObjects(),
            this.subscribeRadarGridPrivileges(),
          ]);
          this.initialized = true;
        }

        this.addBlinkIcons();
        this.addFrameIcons();
        this.addCameraPulsingIcons();
        this.setLayerVisibility();
      }
    },

    showFrameObjectFilter() {
      this.filterFrameById(this.showFrameObjectFilter);
    },

    radarGridCameraMasterFeatures: {
      handler() {
        this.setDataToRadarCameraLayers();
      },
      deep: true,
    },

    radarGridCameraMasterPulsingFeatures: {
      handler() {
        this.setDataToRadarCameraLayers();
      },
      deep: true,
    },

    mapOfCameraIdByRadarId() {
      this.popupViewInstances.forEach((popupInstance) => {
        popupInstance.setMapOfCameraIdByRadarId(this.mapOfCameraIdByRadarId);
      });
    },

    radarGridInfo() {
      this.popupViewInstances.forEach((popupInstance) => {
        popupInstance.setRadarGridInfo(this.radarGridInfo);
      });
    },

    wakeLineFeatures() {
      this.setWakeLineSource();
    },

    futureWakeLineFeatures() {
      this.setFutureWakeLineSource();
    },

    filterRadarGridSet() {
      if (this.isReady) {
        this.setLayerVisibility();
      }
    },
  },
  computed: {
    ...mapStateOfLiveMap({
      filterRadarGrid: (state) => state.filterRadarGrid,
    }),

    filterRadarGridSet() {
      return new Set(this.filterRadarGrid);
    },

    hasViewCameraPrivilege() {
      return this.hasPrivilege(this.RG_PRIVILEGE_SUBJECTS.VIEW_CAMERA);
    },

    hasControlCameraPrivilege() {
      return this.hasPrivilege(this.RG_PRIVILEGE_SUBJECTS.CONTROL_CAMERA);
    },

    hasRateRiskPrivilege() {
      return this.hasPrivilege(this.RG_PRIVILEGE_SUBJECTS.RATE_RISK);
    },

    viewCameraPrivilegeMap() {
      if (!this.hasViewCameraPrivilege) return {};
      return this.createPrivilegeMapBySubject(
        this.RG_PRIVILEGE_SUBJECTS.VIEW_CAMERA,
      );
    },

    controlCameraPrivilegeMap() {
      if (!this.hasControlCameraPrivilege) return {};
      return this.createPrivilegeMapBySubject(
        this.RG_PRIVILEGE_SUBJECTS.CONTROL_CAMERA,
      );
    },

    rateRiskPrivilegeMap() {
      if (!this.hasRateRiskPrivilege) return {};
      return this.createPrivilegeMapBySubject(
        this.RG_PRIVILEGE_SUBJECTS.RATE_RISK,
      );
    },

    hasReadViewCameraPrivilege() {
      return Object.prototype.hasOwnProperty.call(
        this.viewCameraPrivilegeMap,
        this.RG_PRIVILEGE_TYPES.READ,
      );
    },

    cameraHashMap() {
      if (!Array.isArray(this.cameras)) return {};
      return mapKeys(
        this.cameras.filter((camera) => camera && camera.id && camera.hash),
        "hash",
      );
    },

    mapOfCameraIdByRadarId() {
      if (!this.radarGridCameraMaster) return {};
      return Object.values(this.radarGridCameraMaster)
        .filter((radarCamera) => radarCamera)
        .filter(
          (radarCamera) =>
            isString(radarCamera.ci) && 0 < radarCamera.ci.length,
        )
        .filter((radarCamera) => Array.isArray(radarCamera.r))
        .reduce((acc, { r, ci }) => {
          r.forEach((radarId) => {
            if (!acc[radarId]) acc[radarId] = new Set();
            acc[radarId].add(ci);
          });
          return acc;
        }, {});
    },

    showFrameObjectFilter() {
      return ["in", "id"].concat(this.objectIdsToBeShowFrame);
    },

    radarGridCameraMasterFeatures() {
      return this.createRadarGridCameraMasterFeatures(
        Object.values(this.radarGridCameraMaster),
      );
    },

    radarGridCameraMasterPulsingFeatures() {
      return this.createRadarGridCameraPulsingFeatures(
        Object.values(this.radarGridCameraMaster),
      );
    },

    wakeLineFeatures() {
      return Object.keys(this.pastWakes).reduce((acc, objectId) => {
        if (!this.popupViewInstances.has(objectId)) return acc;
        acc.push(
          ...this.createWakeLineFeatures(
            objectId,
            this.pastWakes[objectId] || [],
          ),
        );
        return acc;
      }, []);
    },

    futureWakeLineFeatures() {
      return Object.keys(this.futureWakes).reduce((acc, objectId) => {
        if (!this.popupViewInstances.has(objectId)) return acc;
        acc.push(
          ...this.createFutureWakeLineFeatures(
            objectId,
            this.futureWakes[objectId] || [],
          ),
        );
        return acc;
      }, []);
    },

    rgRiskLevelManagerMap() {
      return Object.keys(this.radarGridObjectFirestoreData).reduce(
        (acc, objectId) => {
          acc[objectId] = this.createRgRiskLevelManager(
            this.radarGridObjectFirestoreData[objectId],
          );
          return acc;
        },
        {},
      );
    },
  },

  methods: {
    async addMapSources() {
      // レーダーグリッドのオブジェクト
      this.map.addSource(this.radarGridSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: this.radarGridObjectFeatures || [],
        },
      });
      // レーダーグリッドの航跡
      this.map.addSource(this.radarGridWakeLineSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: this.wakeLineFeatures || [],
        },
      });
      this.map.addSource(this.radarGridWakeFutureLineSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: this.futureWakeLineFeatures || [],
        },
      });
      // レーダーグリッドのカメラ
      this.map.addSource(this.radarGridCameraMasterSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: this.radarGridCameraMasterFeatures || [],
        },
      });
      this.map.addSource(this.radarGridCameraMasterPulsingSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: this.radarGridCameraMasterPulsingFeatures || [],
        },
      });
    },

    async addMapLayers() {
      this.map.addLayer({
        id: this.radarGridMapLayerId,
        type: "symbol",
        minzoom: this.layersMinZoom,
        source: this.radarGridSourceId,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{objectIcon}",
          "text-justify": "right",
          "icon-rotate": {
            type: "identity",
            property: "cog",
          },
          "text-field": "{trackNumberN}",
          "text-size": 14,
          "text-offset": [-3, 0],
          "text-optional": true,
        },
        paint: {
          "text-color": {
            type: "identity",
            property: "textColor",
          },
        },
      });

      this.map.addLayer({
        id: this.radarGridWakeLineLayerId,
        type: "line",
        source: this.radarGridWakeLineSourceId,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1],
        },
      });

      this.map.addLayer({
        id: this.radarGridWakeFutureLineLayerId,
        type: "line",
        source: this.radarGridWakeFutureLineSourceId,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
        },
      });

      this.map.addLayer({
        id: this.radarGridFrameLayerId,
        type: "symbol",
        minzoom: this.layersMinZoom,
        source: this.radarGridSourceId,
        filter: ["all", ["!=", ["string", ["get", "frameIcon"], ""], ""]],
        layout: {
          "icon-image": "{frameIcon}",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });

      this.map.addLayer({
        id: this.radarGridCameraMasterPulsingLayerId,
        type: "symbol",
        minzoom: this.layersMinZoom,
        source: this.radarGridCameraMasterPulsingSourceId,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": ["get", "icon"],
        },
      });

      this.map.addLayer({
        id: this.radarGridCameraMasterLayerId,
        type: "symbol",
        minzoom: this.layersMinZoom,
        source: this.radarGridCameraMasterSourceId,
        layout: {
          "icon-allow-overlap": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "direction",
          },
          "text-field": "{name}",
          "text-size": 12,
          "text-offset": [
            "case",
            ["boolean", ["get", "isTextTop"], true],
            ["literal", [0, -2]],
            ["literal", [0, 2]],
          ],
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });
    },

    async addClickEvent() {
      this.map.on("click", this.radarGridMapLayerId, (e) => {
        let properties = e.features[0].properties;
        if (this.popupViewInstances.has(properties.id)) {
          this.removePopup(properties.id);
          return;
        }
        this.setPastWakes(properties.id);
        this.setFutureWakes(properties.id);
        let points = { x: `${e.point.x}px`, y: `${e.point.y}px` };
        this.appendPopupView(properties, points);
      });

      this.map.on("mouseenter", this.radarGridMapLayerId, () => {
        this.map.getCanvas().style.cursor = "pointer";
      });

      this.map.on("mouseleave", this.radarGridMapLayerId, () => {
        this.map.getCanvas().style.cursor = "";
      });
    },

    setRadarCameraMasterLayerEvent() {
      this.map.on("click", this.radarGridCameraMasterLayerId, (e) => {
        if (!this.hasReadViewCameraPrivilege) return;
        if (e.originalEvent.cancelBubble) {
          return;
        }
        const cameraId = e.features[0].properties.cameraId;
        if (this.cameraViewInstances.has(cameraId)) {
          this.removeCamera(cameraId);
        } else {
          this.appendCameraView({ objectId: null, cameraId });
        }
      });

      this.map.on("mouseenter", this.radarGridCameraMasterLayerId, () => {
        this.map.getCanvas().style.cursor = this.hasReadViewCameraPrivilege
          ? "pointer"
          : "";
      });

      this.map.on("mouseleave", this.radarGridCameraMasterLayerId, () => {
        this.map.getCanvas().style.cursor = "";
      });
    },

    appendPopupView(properties, points) {
      let ComponentClass = Vue.extend(RgPopup);
      let instance = new ComponentClass({
        propsData: {
          objectId: properties.id,
          elId: `rg-popup-${properties.id}`,
          points: points,
          radarGridCameraMaster: this.radarGridCameraMaster,
          cameraHashMap: this.cameraHashMap,
        },
      });
      instance.$on("onClickTrackBtn", (coordinates) => {
        this.map.flyTo({ center: coordinates, zoom: 13 });
      });
      instance.$on("onClickCloseBtn", (objectId) => {
        this.removePopup(objectId);
        rgPopupDom.remove(); // popupを削除
      });
      instance.$on(
        "onClickZoomPicBtn",
        ({
          objectId,
          trackNumber,
          riskLevel,
          aiseaCameraName,
          imgUrl,
          invokerPosition,
        }) => {
          if (this.zoomPicViewInstances.size !== 0) {
            this.zoomPicViewInstances.forEach((_, key) => {
              if (key === objectId) {
                this.removeZoomPic(key);
              }
            });
          }
          this.appendZoomPicView({
            objectId,
            trackNumber,
            riskLevel,
            aiseaCameraName,
            imgUrl,
            invokerPosition,
          });
        },
      );
      instance.$on(
        "onChangePopupReactiveValues",
        (transferPopupReactiveValues) => {
          if (this.zoomPicViewInstances.size === 0) return;
          const zoomPicViewInstance = this.zoomPicViewInstances.get(
            properties.id,
          );
          if (!zoomPicViewInstance) return;
          const popupReactiveValues = transferPopupReactiveValues();
          zoomPicViewInstance.setPopupReactiveValues(popupReactiveValues);
        },
      );
      instance.$on("onImgUrlChanged", () => {
        if (this.zoomPicViewInstances.size === 0) return;
        const zoomPicViewInstance = this.zoomPicViewInstances.get(
          properties.id,
        );
        if (!zoomPicViewInstance) return;
        zoomPicViewInstance.setHasImgUrlNew(true);
      });
      instance.$on("onClickVideoBtn", (objectId, cameraId) => {
        if (this.cameraViewInstances.size !== 0) {
          this.cameraViewInstances.forEach((value, key) => {
            if (key === cameraId) {
              this.removeCamera(key);
            }
          });
        }
        this.appendCameraView({ objectId, cameraId });
      });
      instance.$on("onClickFeedbackStatusBtn", (objectId, nextRiskLevel) => {
        this.updateRadarGridObjectFirestoreData(
          objectId,
          {
            ...this.radarGridObjectFirestoreData[objectId],
            man_r: nextRiskLevel, // 手動操作の結果をクライアントの分だけ先に更新
          },
          "modified",
        );
        const firestoreData = this.radarGridObjectFirestoreData[objectId];
        const properties = this.crateProperties(firestoreData);
        const feature = turfHelpers.point(
          [properties.log, properties.lat],
          properties,
        );

        this.updateRadarGridObject(firestoreData, feature, "modified");
      });
      instance.setData(properties);
      instance.setRgRiskLevelManagerMap(this.rgRiskLevelManagerMap);
      instance.setRadarGridInfo(this.radarGridInfo.get(instance.objectId));
      instance.setMapOfCameraIdByRadarId(this.mapOfCameraIdByRadarId);
      this.setPopupPrivilege(instance);
      instance.$mount();
      // popupを地図に紐づける
      const rgPopupDom = new Popup({
        className: "rg-popup-wrapper",
        draggable: true,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat([properties.log, properties.lat])
        .setDOMContent(instance.$el)
        .addTo(this.map);
      this.popupViewInstances.set(properties.id, instance);
    },

    setPopupPrivilege(popupInstance) {
      popupInstance.setRadarGridPrivilegeMap({
        viewCameraPrivilegeMap: this.viewCameraPrivilegeMap,
        controlCameraPrivilegeMap: this.controlCameraPrivilegeMap,
        rateRiskPrivilegeMap: this.rateRiskPrivilegeMap,
      });
    },

    setCameraPrivilege(cameraInstance) {
      cameraInstance.setRadarGridPrivilegeMap({
        viewCameraPrivilegeMap: this.viewCameraPrivilegeMap,
        controlCameraPrivilegeMap: this.controlCameraPrivilegeMap,
        rateRiskPrivilegeMap: this.rateRiskPrivilegeMap,
      });
    },

    updatePopupView(properties, isDelete) {
      if (this.popupViewInstances.has(properties.id)) {
        let instance = this.popupViewInstances.get(properties.id);
        if (!isDelete) {
          instance.setData(properties);
          instance.setRgRiskLevelManagerMap(this.rgRiskLevelManagerMap);
          instance.setRadarGridInfo(this.radarGridInfo.get(instance.objectId));
        } else {
          instance.setError("このオブジェクトは監視対象から外れました");
          instance.turnOffMonitoring();
        }
      }
    },

    removePopup(popupId) {
      let instance = this.popupViewInstances.get(popupId);
      instance.$destroy();
      instance.$el.parentNode.removeChild(instance.$el);
      this.popupViewInstances.delete(popupId);
      this.deletePastWakes(popupId);
      this.deleteFutureWakes(popupId);
    },

    async appendZoomPicView({
      objectId,
      trackNumber,
      riskLevel,
      aiseaCameraName,
      imgUrl,
      invokerPosition,
    }) {
      let ComponentClass = Vue.extend(RgZoomPic);
      let instance = new ComponentClass({
        propsData: {
          invokerPosition,
        },
      });
      instance.setPopupReactiveValues({
        trackNumber,
        riskLevel,
        aiseaCameraName,
      });
      instance.setImgUrl(imgUrl);
      instance.$on("onClickReloadBtn", () => {
        if (!this.popupViewInstances.has(objectId)) return;
        const popupViewInstance = this.popupViewInstances.get(objectId);
        instance.setImgUrl(popupViewInstance.getImgUrlLoaded());
        instance.setHasImgUrlNew(false);
      });
      instance.$on("onClickCloseBtn", () => {
        this.removeZoomPic(objectId);
      });
      instance.$mount();
      document.getElementById("radar-grid-layer").append(instance.$el);
      this.zoomPicViewInstances.set(objectId, instance);
      this.detectVueChanges("zoomPicViewInstances");
    },

    async appendCameraView({ objectId, cameraId }) {
      let ComponentClass = Vue.extend(RgCamera);
      let instance = new ComponentClass({
        propsData: {
          // レーダーグリッドオブジェクトからの起動でなければ、カメラアイコンからの起動で一意
          elId: "rg-camera-" + (objectId || cameraId),
          cameraId: cameraId,
          uuid: this.uuid,
          rgInfo: objectId ? this.radarGridInfo.get(objectId) : null,
          cameraHashMap: this.cameraHashMap,
          radarCameraMaster: this.radarGridCameraMaster[cameraId],
        },
      });
      instance.$on("onClickCloseBtn", (cameraId) => {
        this.removeCamera(cameraId);
      });
      this.setCameraPrivilege(instance);
      instance.$mount();
      let status = this.getCameraControlStatus(cameraId);
      instance.setCameraCtlSwitchStatus(status, false, true);
      document.getElementById("radar-grid-layer").append(instance.$el);
      this.cameraViewInstances.set(cameraId, instance);
      this.detectVueChanges("cameraViewInstances");
    },

    getCameraControlStatus(cameraId) {
      const cameraCtl = this.cameraCtlPermissions[cameraId];
      if (!cameraCtl) return this.CAMERA_CTL_STATUS.DISABLED;
      if (cameraCtl.p === null) return this.CAMERA_CTL_STATUS.AUTO;
      const isBefore = moment().utc().isBefore(moment(cameraCtl.to.toDate()));
      if (cameraCtl.p === this.uuid && isBefore) {
        return this.CAMERA_CTL_STATUS.MANUAL;
      } else if (!isBefore) {
        return this.CAMERA_CTL_STATUS.AUTO;
      }

      return this.CAMERA_CTL_STATUS.DISABLED;
    },

    hasCameraControl(cameraId) {
      return (
        this.getCameraControlStatus(cameraId) === this.CAMERA_CTL_STATUS.MANUAL
      );
    },

    hasAnyoneCameraControl(cameraId) {
      return (
        this.hasCameraControl(cameraId) ||
        this.hasSomeoneElseCameraControl(cameraId)
      );
    },

    hasSomeoneElseCameraControl(cameraId) {
      const cameraCtl = this.cameraCtlPermissions[cameraId];
      return (
        cameraCtl &&
        cameraCtl.p !== null &&
        cameraCtl.p !== this.uuid &&
        moment().utc().isBefore(moment(cameraCtl.to.toDate()))
      );
    },

    removeZoomPic(objectId) {
      let instance = this.zoomPicViewInstances.get(objectId);
      instance.$destroy();
      instance.$el.parentNode.removeChild(instance.$el);
      this.zoomPicViewInstances.delete(objectId);
      this.detectVueChanges("zoomPicViewInstances");
    },

    removeCamera(cameraId) {
      let instance = this.cameraViewInstances.get(cameraId);
      instance.$destroy();
      instance.$el.parentNode.removeChild(instance.$el);
      this.cameraViewInstances.delete(cameraId);
      this.detectVueChanges("cameraViewInstances");
    },

    async subscribeRadarGridObjects() {
      let subscribe = await db
        .collection(this.RG_FB_COLLECTIONS.RADAR_GRID_OBJECTS)
        .onSnapshot((querySnapshot) => {
          querySnapshot.docChanges().forEach((change) => {
            this.updateRadarGridObjectFirestoreData(
              change.doc.id,
              change.doc.data(),
              change.type,
            );
            let firestoreData = JSON.parse(JSON.stringify(change.doc.data()));
            let properties = this.crateProperties(firestoreData);
            let feature = turfHelpers.point(
              [properties.log, properties.lat],
              properties,
            );
            this.updateRadarGridObject(firestoreData, feature, change.type);
          });
        });
      this.unsubscribeList.push(subscribe);
    },

    async subscribeRadarGridPrivileges() {
      const subscribe = await db
        .collection(await this.getRadarGridPrivilegeCollectionPath())
        .onSnapshot((querySnapshot) => {
          querySnapshot.docChanges().forEach((change) => {
            this.updateRadarGridPrivileges(
              change.doc.id,
              change.doc.data(),
              change.type,
            );
          });
          this.cameraViewInstances.forEach((cameraInstance) => {
            this.setCameraPrivilege(cameraInstance);
          });

          this.popupViewInstances.forEach((popupInstance) => {
            this.setPopupPrivilege(popupInstance);
          });
        });
      this.unsubscribeList.push(subscribe);
    },

    async subscribeRadarCameraMaster(cameraHashList) {
      return Promise.allSettled(
        chunk(cameraHashList).map(async (chunk) => {
          const subscribe = await db
            .collection(this.RG_FB_COLLECTIONS.RADAR_CAMERA_MASTER)
            .where("a", "in", chunk)
            .onSnapshot((querySnapshot) => {
              querySnapshot.docChanges().forEach((change) => {
                this.updateRadarCameraMaster(
                  change.doc.id,
                  change.doc.data(),
                  change.type,
                );
              });
            });
          this.unsubscribeList.push(subscribe);
        }),
      );
    },

    async getCameraIds() {
      db.collection(this.RG_FB_COLLECTIONS.AI_CAMERA_CONTROL)
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            this.cameraIds.push(doc.id);
            this.subscribeCameraCtl(doc.id);
          });
        });
    },

    async subscribeCameraCtl(cameraId) {
      let subscribe = db
        .collection(this.RG_FB_COLLECTIONS.AI_CAMERA_CONTROL)
        .doc(cameraId)
        .onSnapshot((doc) => {
          this.$set(this.cameraCtlPermissions, doc.id, doc.data());
          if (this.cameraViewInstances.size !== 0) {
            this.cameraViewInstances.forEach((value, _) => {
              if (cameraId === value.cameraId) {
                let status = this.getCameraControlStatus(cameraId);
                value.setCameraCtlSwitchStatus(status);
              }
            });
          }
        });

      this.unsubscribeList.push(subscribe);
    },

    async unsubscribeFireStore() {
      if (this.unsubscribeList !== null && this.unsubscribeList.length > 0) {
        this.unsubscribeList.forEach((value) => {
          value();
        });
        this.unsubscribeList = [];
      }
    },

    updateRadarGridObject(firestoreData, feature, changeType) {
      if (changeType === "added") {
        this.radarGridObjectFeatures.push(feature);
        this.radarGridInfo.set(firestoreData.id, firestoreData.rg);
        this.blinkIcon(feature.properties);
      } else if (changeType === "modified") {
        let res = this.getRadarGridObjectFeatureIndex(feature);
        this.radarGridObjectFeatures.splice(res.index, 1);
        this.radarGridObjectFeatures.push(feature);
        if (res.hasChangedRiskLevel) {
          this.turnOffBlinkIcon(firestoreData.id);
          this.blinkIcon(feature.properties);
        }
        this.updatePopupView(feature.properties, false);
      } else {
        let res = this.getRadarGridObjectFeatureIndex(feature);
        this.radarGridObjectFeatures.splice(res.index, 1);
        this.updatePopupView(feature.properties, true);
        this.radarGridInfo.delete(firestoreData.id);
        this.objectIdsToBeShowFrame = this.objectIdsToBeShowFrame.filter(
          (id) => id !== firestoreData.id,
        );
      }

      this.map.getSource(this.radarGridSourceId).setData({
        type: "FeatureCollection",
        features: this.radarGridObjectFeatures,
      });

      if (this.isWakeLineDisplay(firestoreData.id) && feature.properties) {
        this.setPastWakes(firestoreData.id);
        this.setFutureWakes(firestoreData.id);
      }
    },

    updateRadarGridObjectFirestoreData(subject, firestoreData, changeType) {
      if (!firestoreData) return;
      if (!subject) return;
      if (!changeType) return;

      if (changeType === "added") {
        this.$set(this.radarGridObjectFirestoreData, subject, firestoreData);
      } else if (changeType === "modified") {
        this.$set(this.radarGridObjectFirestoreData, subject, firestoreData);
      } else {
        this.$delete(this.radarGridObjectFirestoreData, subject);
      }
    },

    updateRadarGridPrivileges(subject, firestoreData, changeType) {
      if (!firestoreData) return;
      if (!subject) return;
      if (!changeType) return;

      if (changeType === "added") {
        this.$set(this.radarGridPrivileges, subject, firestoreData);
      } else if (changeType === "modified") {
        this.$set(this.radarGridPrivileges, subject, firestoreData);
      } else {
        this.$delete(this.radarGridPrivileges, subject);
      }
    },

    updateRadarCameraMaster(subject, firestoreData, changeType) {
      if (!firestoreData) return;
      if (!subject) return;
      if (!changeType) return;

      if (changeType === "added") {
        this.$set(this.radarGridCameraMaster, subject, firestoreData);
      } else if (changeType === "modified") {
        this.$set(this.radarGridCameraMaster, subject, firestoreData);
      } else {
        this.$delete(this.radarGridCameraMaster, subject);
      }
    },

    getRadarGridObjectFeatureIndex(feature) {
      let index = null;
      let hasChangedRiskLevel = false;
      for (const [i, val] of Object.entries(this.radarGridObjectFeatures)) {
        if (val.properties.id === feature.properties.id) {
          index = i;
          hasChangedRiskLevel =
            val.properties.riskLevel !== feature.properties.riskLevel;
          break;
        }
      }
      return {
        index: index,
        hasChangedRiskLevel: hasChangedRiskLevel,
      };
    },

    crateProperties(firestoreData) {
      let properties = {};
      const rgRiskLevelManager = this.createRgRiskLevelManager(firestoreData);
      const riskLevelObject =
        this.RADAR_GRID_OBJECT_RISK_LEVEL[rgRiskLevelManager.riskLevel];
      properties.textColor = riskLevelObject.color;
      properties.log = parseFloat(firestoreData.c.longitude);
      properties.lat = parseFloat(firestoreData.c.latitude);
      properties.sog = firestoreData.sog;
      properties.cog = firestoreData.cog;
      properties.objectIcon = rgRiskLevelManager.objectIcon;
      properties.trackNumber = `${firestoreData.n}`;
      properties.trackNumberN = this.isTracked(firestoreData.k)
        ? `Track\n${firestoreData.n}`
        : `Target\n${firestoreData.n}`;
      properties.k = firestoreData.k;
      properties.id = firestoreData.id;
      properties.frameIcon = rgRiskLevelManager.frameIconId;
      properties.riskLevel = rgRiskLevelManager.riskLevel;
      properties.man_r = firestoreData.man_r;
      properties.ai_r = firestoreData.ai_r;
      if (firestoreData.p.length !== 0) {
        properties.cameraId = firestoreData.p[0].ci;
        properties.confidence = firestoreData.p[0].co
          ? `${Math.floor(firestoreData.p[0].co * 100)}`
          : "---";
        properties.imgUrl = firestoreData.p[0].p;
        properties.shootingDate = firestoreData.p[0].t
          ? this.toJST(firestoreData.p[0].t.seconds, `${firestoreData.n}`)
          : "---";
      }
      return properties;
    },

    isCameraMasterTextTop(direction) {
      return direction > 90 && direction < 270;
    },

    isCameraSelected(cameraId, selectedCameraId = "") {
      return cameraId && cameraId === selectedCameraId;
    },

    createCameraIconText(cameraId) {
      return this.cameraViewInstances.has(cameraId) ||
        this.hasAnyoneCameraControl(cameraId)
        ? this.RADAR_GRID_CAMERA_MASTER_ICON.SELECTED
        : this.RADAR_GRID_CAMERA_MASTER_ICON.UNSELECTED;
    },

    cameraPulsingIconText(cameraId) {
      if (this.hasAnyoneCameraControl(cameraId)) {
        return this.radarGridCameraMasterPulsingManualImageId;
      } else if (this.cameraViewInstances.has(cameraId)) {
        return this.radarGridCameraMasterPulsingImageId;
      }
      return null;
    },

    createRadarGridCameraMasterFeatures(
      radarCameraMasters, // Array<RadarCameraMaster>
    ) {
      // FeatureCollection
      return turfHelpers.featureCollection(
        radarCameraMasters
          .filter((cameraMaster) => cameraMaster && cameraMaster.l)
          .map((cameraMaster) => {
            return turfHelpers.point(
              [cameraMaster.l.longitude, cameraMaster.l.latitude],
              {
                cameraId: cameraMaster.ci,
                name: this.lookupCameraNameByHash(cameraMaster.a),
                direction: cameraMaster.p,
                icon: this.createCameraIconText(cameraMaster.ci),
                isTextTop: this.isCameraMasterTextTop(cameraMaster.p),
              },
            );
          }),
      );
    },

    lookupCameraNameByHash(hash) {
      if (!this.cameraHashMap[hash]) return "";
      return this.cameraHashMap[hash].name || "";
    },

    createRadarGridCameraPulsingFeatures(
      radarCameraMasters, // Array<RadarCameraMaster>
    ) {
      // FeatureCollection
      return turfHelpers.featureCollection(
        radarCameraMasters
          .filter((cameraMaster) => cameraMaster && cameraMaster.l)
          .map((cameraMaster) => {
            return turfHelpers.point(
              [cameraMaster.l.longitude, cameraMaster.l.latitude],
              {
                cameraId: cameraMaster.ci,
                icon: this.cameraPulsingIconText(cameraMaster.ci),
              },
            );
          }),
      );
    },
    setDataToRadarCameraLayers() {
      if (this.isReady) {
        const radarGridCameraMasterSource = this.map.getSource(
          this.radarGridCameraMasterSourceId,
        );
        if (radarGridCameraMasterSource) {
          radarGridCameraMasterSource.setData(
            this.radarGridCameraMasterFeatures,
          );
        }
        const radarGridCameraMasterPulsingSource = this.map.getSource(
          this.radarGridCameraMasterPulsingSourceId,
        );
        if (radarGridCameraMasterPulsingSource) {
          radarGridCameraMasterPulsingSource.setData(
            this.radarGridCameraMasterPulsingFeatures,
          );
        }
      }
    },

    addBlinkIcons() {
      this.map.addImage(
        this.getBrinkIconId(this.RG_RISK_LEVEL.CAUTION),
        this.getBrinkIcon(this.RG_RISK_LEVEL.CAUTION),
        { pixelRatio: 2 },
      );
      this.map.addImage(
        this.getBrinkIconId(this.RG_RISK_LEVEL.DANGER),
        this.getBrinkIcon(this.RG_RISK_LEVEL.DANGER),
        { pixelRatio: 2 },
      );
      this.map.addImage(
        this.getBrinkIconId(this.RG_RISK_LEVEL.SAFE),
        this.getBrinkIcon(this.RG_RISK_LEVEL.SAFE),
        { pixelRatio: 2 },
      );

      this.map.addImage(
        this.getBrinkIconId(this.RG_RISK_LEVEL.CAUTION, false),
        this.getBrinkIconOfOtherKind(this.RG_RISK_LEVEL.CAUTION),
        { pixelRatio: 2 },
      );
      this.map.addImage(
        this.getBrinkIconId(this.RG_RISK_LEVEL.DANGER, false),
        this.getBrinkIconOfOtherKind(this.RG_RISK_LEVEL.DANGER),
        { pixelRatio: 2 },
      );
      this.map.addImage(
        this.getBrinkIconId(this.RG_RISK_LEVEL.SAFE, false),
        this.getBrinkIconOfOtherKind(this.RG_RISK_LEVEL.SAFE),
        { pixelRatio: 2 },
      );
    },

    addFrameIcons() {
      this.map.addImage(
        this.getFrameIconId(this.RG_RISK_LEVEL.CAUTION),
        this.getFrameIcon(this.RG_RISK_LEVEL.CAUTION),
        { pixelRatio: 2 },
      );
      this.map.addImage(
        this.getFrameIconId(this.RG_RISK_LEVEL.DANGER),
        this.getFrameIcon(this.RG_RISK_LEVEL.DANGER),
        { pixelRatio: 2 },
      );
      this.map.addImage(
        this.getFrameIconId(this.RG_RISK_LEVEL.SAFE),
        this.getFrameIcon(this.RG_RISK_LEVEL.SAFE),
        { pixelRatio: 2 },
      );
    },

    addCameraPulsingIcons() {
      this.map.addImage(
        this.radarGridCameraMasterPulsingImageId,
        this.createCameraPulsing(this.map),
        { pixelRatio: 2 },
      );

      this.map.addImage(
        this.radarGridCameraMasterPulsingManualImageId,
        this.createCameraPulsingManual(this.map),
        { pixelRatio: 2 },
      );
    },

    async blinkIcon(props) {
      let coordinates = [props.log, props.lat];
      let blinkIconSourceId = props.id;

      this.turnOffBlinkIcon(blinkIconSourceId);
      this.map.addSource(blinkIconSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: coordinates,
              },
            },
          ],
        },
      });

      let blinkIconLayerId = props.id;
      this.map.addLayer({
        id: blinkIconLayerId,
        type: "symbol",
        source: blinkIconSourceId,
        layout: {
          "icon-image": this.createRgRiskLevelManager(props).brinkIconId,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });

      this.$set(
        this.timeoutIdsOfShowFrame,
        blinkIconSourceId,
        window.setTimeout(() => {
          this.turnOffBlinkIcon(blinkIconSourceId);
          this.objectIdsToBeShowFrame.push(blinkIconSourceId);
          this.$delete(this.timeoutIdsOfShowFrame, blinkIconSourceId);
        }, 5000),
      );
    },

    turnOffBlinkIcon(blinkIconSourceId) {
      if (this.map.getLayer(blinkIconSourceId)) {
        this.map.removeLayer(blinkIconSourceId);
      }
      if (this.map.getSource(blinkIconSourceId)) {
        this.map.removeSource(blinkIconSourceId);
      }

      this.objectIdsToBeShowFrame = this.objectIdsToBeShowFrame.filter(
        (id) => id !== blinkIconSourceId,
      );
      const timeoutId = this.timeoutIdsOfShowFrame[blinkIconSourceId];
      if (timeoutId) {
        this.$delete(this.timeoutIdsOfShowFrame, blinkIconSourceId);
        window.clearTimeout(timeoutId);
      }
    },

    toJST(unix, trackNumber) {
      let momentObjJst = moment(unix, "X").tz("Asia/Tokyo");
      this.setLastObjectTracking(momentObjJst, trackNumber);
      return momentObjJst.format("YYYY/MM/DD/ HH:mm");
    },

    setLastObjectTracking(momentObj, trackNumber) {
      if (this.lastObjectTracking.trackNo) {
        if (momentObj.isAfter(this.lastObjectTracking.momentObj)) {
          this.lastObjectTracking.trackNo = trackNumber;
          this.lastObjectTracking.startTime = momentObj.toISOString();
          this.lastObjectTracking.momentObj = momentObj;
        }
      } else {
        this.lastObjectTracking.trackNo = trackNumber;
        this.lastObjectTracking.startTime = momentObj.toISOString();
        this.lastObjectTracking.momentObj = momentObj;
      }
    },

    async setWakeLineSource() {
      const source = this.map.getSource(this.radarGridWakeLineSourceId);
      if (source !== null) {
        source.setData({
          type: "FeatureCollection",
          features: this.wakeLineFeatures,
        });
      }
    },

    async setFutureWakeLineSource() {
      const source = this.map.getSource(this.radarGridWakeFutureLineSourceId);
      if (source !== null) {
        source.setData({
          type: "FeatureCollection",
          features: this.futureWakeLineFeatures,
        });
      }
    },

    createWakeLineFeatures(
      id, //string
      wayPoints, // Array<Point>
    ) {
      return wayPoints.reduce((acc, _val, index, self) => {
        if (index === 0) return acc;
        acc.push(
          this.createWakeLineFeature({
            d: moment.utc(self[index].d).local().format("YYYY/MM/DD HH:mm"),
            point: [
              [self[index - 1].lon, self[index - 1].lat],
              [self[index].lon, self[index].lat],
            ],
            color: WakeLineUtil.getVelocityColor(self[index - 1].v),
            opacity: 0.9,
            id,
          }),
        );
        return acc;
      }, []);
    },

    createFutureWakeLineFeatures(
      id, //string
      wayPoints, // Array<Point>
    ) {
      return wayPoints.reduce((acc, _val, index, self) => {
        if (index === 0) return acc;
        acc.push(
          this.createWakeLineFeature({
            d: moment.utc(self[index].d).local().format("YYYY/MM/DD HH:mm"),
            point: [
              [self[index - 1].lon, self[index - 1].lat],
              [self[index].lon, self[index].lat],
            ],
            color: "#BDBDBD", // 航跡予測は色が固定
            opacity: 0.9,
            id,
          }),
        );
        return acc;
      }, []);
    },

    async setPastWakes(radarObjectId) {
      if (!(isString(radarObjectId) && radarObjectId.length > 0)) return;
      const pastWakes = await radarGridObjectService
        .pathByRadarGridObjectIdForOneDay(radarObjectId)
        .then((res) => res.data.p)
        .catch((_error) => []);
      if (this.popupViewInstances.has(radarObjectId)) {
        this.$set(this.pastWakes, radarObjectId, pastWakes);
      }
    },

    async setFutureWakes(radarObjectId) {
      if (!(isString(radarObjectId) && radarObjectId.length > 0)) return;
      const futureWakes = await radarGridObjectService
        .pathForecast(radarObjectId)
        .then((res) => res.data.f)
        .catch((_error) => []);
      if (this.popupViewInstances.has(radarObjectId)) {
        this.$set(this.futureWakes, radarObjectId, futureWakes);
      }
    },

    async deletePastWakes(radarObjectId) {
      if (!(isString(radarObjectId) && radarObjectId.length > 0)) return;
      this.$delete(this.pastWakes, radarObjectId);
    },

    async deleteFutureWakes(radarObjectId) {
      if (!(isString(radarObjectId) && radarObjectId.length > 0)) return;
      this.$delete(this.futureWakes, radarObjectId);
    },

    isWakeLineDisplay(radarGridObjectId) {
      return this.popupViewInstances.has(radarGridObjectId);
    },

    createWakeLineFeature(line) {
      return {
        type: "Feature",
        properties: {
          color: line.color,
          opacity: 0.9,
          id: line.id,
        },
        geometry: {
          type: "LineString",
          coordinates: line.point,
        },
      };
    },

    hasPrivilege(subject) {
      const privilege = this.radarGridPrivileges[subject];
      return !!privilege && Array.isArray(privilege.granted);
    },

    createPrivilegeMapBySubject(subject) {
      if (!this.hasPrivilege(subject)) return {};
      return mapKeys(
        this.radarGridPrivileges[subject].granted.filter(
          (grantedItem) => grantedItem && grantedItem.type,
        ),
        "type",
      );
    },

    async fetchCameras() {
      const response = await AiseaApiFactory.get("enterpriseCamera").search();
      if (response && response.data && Array.isArray(response.data.cameras)) {
        return response.data.cameras;
      }
      return [];
    },

    async setCameras() {
      try {
        this.cameras = await this.fetchCameras();
      } catch (error) {
        Vue.$log.debug(error);
      }
    },

    filterFrameById(filters) {
      if (!this.map) return;
      if (this.map.getLayer(this.radarGridFrameLayerId)) {
        this.map.setFilter(this.radarGridFrameLayerId, ["all", filters]);
      }
    },

    changeActiveRadarGridObjectList() {
      this.isActiveRadarGridObjectList = !this.isActiveRadarGridObjectList;
    },

    async flyAndAppendPopupObject(radarObjectId) {
      try {
        if (this.isFocusingRadarObject) return;
        this.isFocusingRadarObject = true;
        const targetObjectFeature = this.radarGridObjectFeatures.find(
          (radarObjectFeature) =>
            radarObjectFeature &&
            radarObjectFeature.properties &&
            radarObjectFeature.properties.id === radarObjectId,
        );
        const coordinates =
          targetObjectFeature &&
          targetObjectFeature.geometry &&
          targetObjectFeature.geometry.coordinates;
        const { properties } = targetObjectFeature;
        if (!coordinates || !properties) return;

        this.map
          .getCanvasContainer()
          .classList.add("is-focusing-radar-grid-object");
        this.map.flyTo({
          center: coordinates,
          zoom: 15,
        });
        this.map.once("moveend", () => {
          try {
            if (!this.popupViewInstances.has(radarObjectId)) {
              this.setPastWakes(properties.id);
              this.setFutureWakes(properties.id);
              const points = this.map.project(coordinates);
              this.appendPopupView(properties, {
                x: `${points.x}px`,
                y: `${points.y}px`,
              });
            }
          } finally {
            this.map
              .getCanvasContainer()
              .classList.remove("is-focusing-radar-grid-object");
            this.isFocusingRadarObject = false;
          }
        });
      } catch (_e) {
        this.map
          .getCanvasContainer()
          .classList.remove("is-focusing-radar-grid-object");
        this.isFocusingRadarObject = false;
      }
    },
    // vue2にMapの変更を検知させるためにポインタを付け替える
    // vue2はMapをサポートしておらず、通常の変更をしてもリアクティブにならない
    detectVueChanges(
      dataPath, // :string
    ) {
      if (!dataPath) return;
      const tmp = this[dataPath];
      if (!tmp.constructor) return;
      this[dataPath] = new tmp.constructor();
      this[dataPath] = tmp;
    },

    async cleanupPopups() {
      this.popupViewInstances.forEach((_, popupId) => {
        this.removePopup(popupId);
      });
    },

    async cleanupZoomPics() {
      this.zoomPicViewInstances.forEach((_, objectId) => {
        this.removeZoomPic(objectId);
      });
    },

    async cleanupCameras() {
      this.cameraViewInstances.forEach((_, cameraId) => {
        this.removeCamera(cameraId);
      });
    },

    async cleanup() {
      await this.cleanupPopups();
      await this.cleanupZoomPics();
      await this.cleanupCameras();
      await this.unsubscribeFireStore();
      clearTimeout(this.countTimer);
    },

    setLayerVisibility() {
      const { WAKE_FORECAST } = RADAR_GRID_CATEGORY;
      const calcVisibilityProperty = (filterId) => {
        if (this.filterRadarGridSet.has(String(filterId))) return "visible";
        return "none";
      };

      this.map.setLayoutProperty(
        this.radarGridWakeFutureLineLayerId,
        "visibility",
        calcVisibilityProperty(WAKE_FORECAST),
      );
    },
  },

  async created() {
    let user = await AuthService.getUser();
    this.uuid = `${user.id}-${user.account_id}`;
    await this.getCameraIds();
    await this.setCameras();
    await this.subscribeRadarCameraMaster(Object.keys(this.cameraHashMap));
    this.countTimer = setInterval(() => {
      this.timeCount = (this.timeCount + 1) % SECONDS_IN_A_DAY;
    }, INTERVAL_CALC_OBJECT_ELAPSED_TIME);
  },

  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss">
#radar-grid-layer {
  width: 100%;
}
.is-focusing-radar-grid-object {
  pointer-events: none;
}
.rg-popup-wrapper {
  z-index: 11;
  .mapboxgl-popup-tip {
    display: none;
  }
  .mapboxgl-popup-content {
    display: contents;
  }
}
</style>
